import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { operationType, selectDataType } from "../../../../../services/types";
import { TextInput } from "../../../../form/textInput";
import { DateInput } from "../../../../form/date";
import { getvaluesUsingVariables, updateNodeValue } from "../../../../../services/method";
import { Checkboxs } from "../../../../form/checkboxs";
import { Select } from "../../../../form/select";
import { Main, Styled } from "../googleDrive/style";
import { Switch } from "../../../../form/switch";
import MicrosoftLoginWrapper from "../../../../auth/microsoft";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type AzureProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function AzureStorage({ id, data, setChildMinimize, setChildSettings }: AzureProps) {
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'List Files', refId: 'list_files', isActive: true }, { id: 2, name: 'Upload File', refId: 'upload' },
      { id: 3, name: 'Let AI Agent Decide', refId: 'agent_decide' }
   ]);
   const { getNodes, setNodes } = useReactFlow();
   const credential = { headers: { Authorization: `Bearer ${data.value?.token}`}};
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
      
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         if (data.currentCategory === 'Actions') {
            setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === 'upload' })));
            setNodes(nodes => nodes.map(node => {
               if (node.id === id) {
                  return { ...node, data: { ...node.data, value: { ...node.data.value, request: 'upload' }}}
               } else return node;
            }));
         }
         else updateNodeValue(id, setNodes, { request: requests[0].refId });
      }
      else setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === data.value.request })));
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleAuthenticate = async (data: any) => {
      updateNodeValue(id, setNodes, { token: data.accessToken });
      const containers = (await axios.get('/storage/azure-blob/get_containers?storage_account=azurefunctionblock', { headers: { Authorization: `Bearer ${data.accessToken}`}})).data.containers;
      updateNodeValue(id, setNodes, { containers: containers.map((container: any, index: number) => ({ id: index+1, name: container.name, isActive: !index })) });
   }
   
   // ---
   const handleChangeSelect = (type: 'request' | 'container' , selectId: number) => {
      if (type === 'request') {
         const refId = requests.find(obj => obj.id === selectId)?.refId;

         setRequests((requests) => requests.map((request) => {
            if (request.id === selectId) return { ...request, isActive: true };
            else return { ...request, isActive: false };
         }));
         setNodes(nodes => nodes.map(node => {
            if (node.id === id) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, request: refId }}}
            } else return node;
         }));

      } else if (type === 'container') {
         updateNodeValue(id, setNodes, { containers: data.value.containers.map((container: any) => ({ ...container, isActive: container.id === selectId })) });
      }
   }
   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      const value = e.target.value;
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
   }
   // ---
   const hanedleButtons = async (type: 'fetch' | 'download' | 'delete') => {
      try {
         updateNodeValue(id, setNodes, { isLoading: true });
         if (type === 'fetch') {
            let request = { 
               container_name: data.value.containers.find((container: any) => container.isActive)?.name,
               name_prefix: data.value.text_contains,
               extension: data.value.extension,
               modified_time: data.value.modified_time
            } 
            // Variables system
            request = { ...request, ...getvaluesUsingVariables({ name_prefix: request.name_prefix, extension: request.extension }, getNodes())};
            
            // @ts-ignore
            for (let key in request) { if (!request[key]) delete request[key] }
            if (request.modified_time) request.modified_time = new Date(data.value.modified_time).toISOString();
   
            const response = (await axios.post('/storage/azure-blob/list_files?storage_account=azurefunctionblock', request, credential)).data;         
            updateNodeValue(id, setNodes, { isLoading: false, files: response.files.map((file: any) => ({ id: file.id, name: file.name, type: file.content_type })) });
   
         } else if (type === 'download') {
            const filter = data.value.files.filter((file: any) => file.isActive && !file.url).map((file: any) => file.name);
   
            if (filter.length) {
               const fetch = (await axios.post(
                  '/storage/azure-blob/download_files?storage_account=azurefunctionblock', 
                  { blob_names: filter, container_name: data.value.containers.find((container: any) => container.isActive)?.name }, credential
               )).data.downloaded_files;
               const reMap = data.value.files.map((file: any) => ({ ...file, url: file.url || fetch.find((f: any) => f.id === file.id)?.uploadedUrl || '' }))
               updateNodeValue(id, setNodes, { isLoading: false, files: reMap });
            } else {
               updateNodeValue(id, setNodes, { isLoading: false });
            }
            
         } else if (type === 'delete') {
            const filter = data.value.files.filter((file: any) => file.isActive);
            const container_name = data.value.containers.find((container: any) => container.isActive)?.name;
   
            for (const item of filter) {
               await axios.delete(`/storage/azure-blob/delete/${container_name}/${item.name}?storage_account=azurefunctionblock`, credential);
            }  
            updateNodeValue(id, setNodes, { isLoading: false, files: data.value.files.filter((file: any) => !filter.some((f: any) => f.id === file.id)) });
         }

      } catch {
         updateNodeValue(id, setNodes, { isLoading: false });
      }
   }
   // ---
   const filesLengthActive = data.value?.files?.filter((item: any) => item.isActive)?.length;
   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         { isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null }
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            styleParent={ (data.value?.request === 'list_files' && data.value?.files?.length ? { right: '-136.5px' } : {}) }
            style={{ width: '420px' }}
         ><Styled>
            <div className="action">
               <MicrosoftLoginWrapper
                  value={ !data.value?.token ? 'Authenticate' : data.value?.request === 'list_files' ? 'Re-Auth' : 'Re-Authenticate' }
                  scope="https://storage.azure.com/user_impersonation"
                  handleAuthenticate={ handleAuthenticate }
               />
               { data.value?.request === 'list_files' && data.value?.token ? <>
                  <button className="btn1 withBorder" onClick={ _=> hanedleButtons('fetch') } disabled={ data.value?.isLoading }>Fetch</button> 
                  { filesLengthActive ? <>
                     <button className="btn1 withBorder" onClick={ _=> hanedleButtons('download') } disabled={ data.value?.isLoading }>Download</button> 
                     <button className="btn1 withBorder delete" onClick={ _=> hanedleButtons('delete') } disabled={ data.value?.isLoading }>Delete</button>
                  </> : null }
               </> : null }
            </div>
            <Select title="Request" data={ requests } handleSelectItem={ selectId => handleChangeSelect('request', selectId) } isSmall={ true } />
            { data.value?.containers ? <Select title="Container" data={ data.value.containers } handleSelectItem={ selectId => handleChangeSelect('container', selectId) } isSmall={ true } /> : null }
            { data.value?.request === 'list_files' ? <>
               <TextInput keyRef="text_contains" title="Contains" placeholder="text_contains" value={ data.value?.text_contains || '' } handleChange={ handleChangeInput } />
               <TextInput keyRef="extension" title="Extension" placeholder="extension" value={ data.value?.extension || '' } handleChange={ handleChangeInput } />
               <DateInput title="Modified" value={ data.value?.modified_time || '' } handleChange={ (value: string) => { updateNodeValue(id, setNodes, { modified_time: value }) }} />
               <Switch title="Start autopilot (with loop)" className="autopilot" value={ !!data.value?.isAutopilot } setValue={ () => updateNodeValue(id, setNodes, { isAutopilot: !data.value?.isAutopilot }) } />
               <p>Please select your filter carefully to avoid downloading incorrect or unintended files!</p>
               { data.value?.files?.length ? <div className="filesEdit">
                  <h3 className="title">Files ({ filesLengthActive }/{ data.value.files.length })</h3>
                  <div className="scroll nowheel">
                     <Checkboxs 
                        data={ data.value.files.map((file: any) => ({ ...file, isNotActive: !file.url })) } 
                        handelCheckbox={ (fileId: any, value?: boolean) => { 
                           updateNodeValue(id, setNodes, { files: data.value.files.map((item: any) => {
                              if (item.id === fileId) return ({ ...item, isActive: !value })
                              else return item;
                           })});
                        }} 
                     />
                  </div>
               </div> : null }
            </> : null}
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}